import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { registerInvitedUser } from '../api/adminApi';
import SchoolPublicSite from './SchoolPublicSite';
import { schoolLogoSrc } from '../utils/schoolLogo';
import { toStudentSignInEmail } from '../utils/studentLogin';
import RefreshAppButton from './RefreshAppButton';

const AUTH_CONTINUE_URL = process.env.REACT_APP_AUTH_CONTINUE_URL || 'https://mpsvns.web.app';

export default function LoginPanel() {
  const [mode, setMode] = useState('login');
  /** Staff/parent email, or student login id (firstname+admno, @mpsvns.in added automatically). */
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!auth) return;
    setError('');
    setInfo('');
    try {
      const signInEmail = toStudentSignInEmail(email);
      await signInWithEmailAndPassword(auth, signInEmail, password);
    } catch (err) {
      const code = err.code || '';
      if (code.includes('user-not-found') || code.includes('invalid-credential')) {
        setError('No account found for this login. Ask the school admin to import student logins CSV, or check login id and password.');
      } else {
        setError(err.message || 'Sign-in failed');
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setInfo('');
    try {
      await registerInvitedUser({ email, password });
      setInfo('Account created. You are now signed in.');
    } catch (err) {
      setError(err.message || 'Registration failed');
    }
  };

  const handlePasswordReset = async () => {
    if (!auth || !email.trim()) {
      setError('Enter your login id or email above, then click Forgot password.');
      return;
    }
    setError('');
    try {
      await sendPasswordResetEmail(auth, toStudentSignInEmail(email), {
        url: AUTH_CONTINUE_URL,
        handleCodeInApp: false,
      });
      setInfo('Password reset email sent. Check your inbox.');
    } catch (err) {
      setError(err.message || 'Could not send reset email');
    }
  };

  return (
    <div className="mps-app mps-login-app">
      <div className="mps-login-split">
        <div className="mps-login-public">
          <SchoolPublicSite />
        </div>
        <aside className="mps-login-aside" id="erp-login" tabIndex={-1}>
          <div className="mps-login-aside-inner mps-card">
            <div className="mps-login-welcome-banner">
              <div className="mps-login-logo-row">
                <div className="mps-app-logo-wrap mps-app-logo-wrap-lg" aria-hidden="true">
                  <img src={schoolLogoSrc()} alt="Modern Public School logo" width={64} height={64} />
                </div>
              </div>
              <p className="mps-login-welcome-sign" aria-hidden="true">🙏</p>
              <h2 className="mps-login-welcome-title">Welcome to</h2>
              <p className="mps-login-welcome-school">MODERN PUBLIC SCHOOL APP</p>
            </div>
            <p className="mps-login-aside-kicker">Staff, parents &amp; students</p>
            <h2 className="mps-login-aside-title">ERP portal</h2>
            <p className="mps-muted mps-login-aside-lead">
              Staff and parents use their email. Students use <strong>first name + admission number</strong> (school login id) and date-of-birth
              password — see notice below.
            </p>
            <div className="mps-tabs">
              <button type="button" className={`mps-tab ${mode === 'login' ? 'mps-tab-active' : ''}`} onClick={() => setMode('login')}>
                Sign in
              </button>
              <button type="button" className={`mps-tab ${mode === 'register' ? 'mps-tab-active' : ''}`} onClick={() => setMode('register')}>
                Register (invited)
              </button>
            </div>
            <h3 className="mps-login-form-heading">{mode === 'login' ? 'Sign in' : 'Complete registration'}</h3>
            {mode === 'register' ? (
              <p className="mps-msg mps-msg-info">Use the email address your admin invited. Choose a password to activate your account.</p>
            ) : (
              <p className="mps-msg mps-msg-info">
                <strong>Students:</strong> Login id is <code>firstname</code> + <code>adm no</code> (example: <code>aditya1634</code>). Password is DOB as{' '}
                <code>DDMMYYYY</code>. The app adds <code>@mpsvns.in</code> for sign-in — this is not a real email inbox.
              </p>
            )}
            {error ? <div className="mps-msg mps-msg-error">{error}</div> : null}
            {info ? <div className="mps-msg mps-msg-success">{info}</div> : null}
            <form onSubmit={mode === 'login' ? handleSignIn : handleRegister}>
              <div className="mps-field" style={{ marginBottom: 12 }}>
                <label htmlFor="email">{mode === 'login' ? 'Email or student login id' : 'Email'}</label>
                <input
                  id="email"
                  type="text"
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete={mode === 'login' ? 'username' : 'email'}
                  placeholder={mode === 'login' ? 'you@school.com or aditya1634' : 'invited@email.com'}
                  required
                />
              </div>
              <div className="mps-field" style={{ marginBottom: 12 }}>
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  required
                />
              </div>
              {mode === 'register' ? (
                <div className="mps-field" style={{ marginBottom: 16 }}>
                  <label htmlFor="confirm">Confirm password</label>
                  <input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
                </div>
              ) : null}
              <button type="submit" className="mps-btn mps-btn-block">
                {mode === 'login' ? 'Sign in' : 'Create account'}
              </button>
              {mode === 'login' ? (
                <p className="mps-muted" style={{ marginTop: 12, fontSize: '0.9rem' }}>
                  <button type="button" className="mps-link-btn" onClick={handlePasswordReset}>
                    Forgot password?
                  </button>{' '}
                  Student reset emails are not monitored; contact the school office if you are stuck.
                </p>
              ) : null}
            </form>
            <div className="mps-refresh-app-row">
              <RefreshAppButton label="Get latest update" />
              <span className="mps-muted mps-refresh-app-hint">Clears cache &amp; reloads if you don&apos;t see new features</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export function AppHeader({ title, user, profile, onSignOut }) {
  return (
    <header className="mps-header">
      <div className="mps-header-brand">
        <div className="mps-app-logo-wrap" aria-hidden="true">
          <img src={schoolLogoSrc()} alt="" width={48} height={48} />
        </div>
        <div>
          <h1>Modern Public School</h1>
          <div className="mps-header-meta">{title}</div>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="mps-header-meta">
          {profile?.name || user?.email}
          {profile?.role ? (
            <span
              className={`mps-badge mps-badge-${
                profile.role === 'SUPERUSER'
                  ? 'admin'
                  : profile.role === 'COORDINATOR'
                    ? 'coordinator'
                    : profile.role === 'TEACHER'
                    ? 'teacher'
                    : profile.role === 'STUDENT'
                      ? 'student'
                      : 'parent'
              }`}
              style={{ marginLeft: 8 }}
            >
              {profile.role}
            </span>
          ) : null}
        </div>
        <div className="mps-header-actions">
          <RefreshAppButton />
          <button type="button" className="mps-btn mps-btn-secondary mps-btn-sm" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
